/**
 * Guest API Client
 * Handles API calls for guest management and device sharing
 */

import { apiRequestProtected } from '../utils/axios'
import type {
  GuestInvitation,
  GuestInvitationResponse,
  GuestListResponse,
  GuestListFilters,
  RevokeGuestResponse,
  SharedDevicesResponse,
} from '../types/guest'

/**
 * Map frontend access levels to backend format
 */
function mapAccessLevel(frontendLevel: string): string {
  const mapping: Record<string, string> = {
    VIEW_ONLY: 'READ_ONLY',
    CONTROL: 'READ_WRITE',
    FULL: 'FULL_CONTROL',
  }
  return mapping[frontendLevel] || frontendLevel
}

/**
 * Invite a guest user to access specific devices
 * USER perspective - owner invites guest
 *
 * Uses the bulk endpoint for optimal performance (one API call instead of N calls)
 *
 * @param invitation - Guest invitation data (email, deviceIds, accessLevel)
 * @returns Invitation response with guest user and access grants
 */
export async function inviteGuest(invitation: GuestInvitation): Promise<GuestInvitationResponse> {
  try {
    const deviceCount = invitation.deviceIds.length
    console.log('📧 Inviting guest:', invitation.email, 'to', deviceCount, 'device(s)')

    // Use bulk endpoint for optimal performance
    const backendAccessLevel = mapAccessLevel(invitation.accessLevel)

    const response = await apiRequestProtected('POST', '/user/invite-guest-bulk', {
      guestEmail: invitation.email,
      deviceIds: invitation.deviceIds,
      accessLevel: backendAccessLevel,
      skipEmail: false,
    })

    // Extract data from response (API returns { success: true, data: {...} })
    const data = response.data || response

    const finalResponse: GuestInvitationResponse = {
      message: data.message || `Guest invited to ${data.devices_invited} device(s)`,
      guestUser: {
        id: '',
        email: invitation.email,
        status: data.status || 'pending',
      },
      accessGrants: [], // Bulk endpoint doesn't return individual grants
      emailSent: data.emailSent || false,
    }

    console.log('✅ Guest invited successfully:', finalResponse)
    console.log(
      `   - Invited: ${data.devices_invited}, Skipped: ${data.devices_skipped}, Failed: ${data.devices_failed}`
    )

    return finalResponse
  } catch (error: any) {
    console.error('❌ Failed to invite guest:', error)
    throw error
  }
}

/**
 * List all guests invited by the current user
 * USER perspective - view invited guests
 *
 * @param page - Page number (1-indexed)
 * @param limit - Results per page
 * @param filters - Optional filters (deviceId, status)
 * @returns Paginated list of guest access grants
 */
export async function listGuests(
  page: number = 1,
  limit: number = 10,
  filters?: GuestListFilters
): Promise<GuestListResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (filters?.deviceId) {
      params.append('deviceId', filters.deviceId.toString())
    }

    if (filters?.status) {
      params.append('status', filters.status)
    }

    const response = await apiRequestProtected('GET', `/user/guests?${params.toString()}`)

    // API returns { success: true, data: { guests, pagination } }
    // Extract the inner data object to match GuestListResponse type
    const apiResponse = response.data || response

    // Transform API response to match frontend types
    const guests = (apiResponse.guests || []).map((guest: any) => ({
      id: guest.access_id,
      guestEmail: guest.guest_email,
      deviceId: guest.device_id,
      deviceName: guest.device_name,
      accessLevel: guest.access_level,
      status: guest.status,
      createdAt: guest.created_at,
      revokedAt: guest.revoked_at,
    }))

    return {
      guests,
      pagination: apiResponse.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
    }
  } catch (error: any) {
    console.error('❌ Failed to list guests:', error)
    throw error
  }
}

/**
 * Revoke guest access to a device
 * USER perspective - owner revokes access
 *
 * @param accessId - Guest access grant ID
 * @returns Revoke confirmation response
 */
export async function revokeGuestAccess(accessId: string): Promise<RevokeGuestResponse> {
  try {
    console.log('🚫 Revoking guest access:', accessId)

    const response = await apiRequestProtected('DELETE', `/user/guests/${accessId}`)

    console.log('✅ Guest access revoked:', response)
    return response
  } catch (error: any) {
    console.error('❌ Failed to revoke guest access:', error)
    throw error
  }
}

/**
 * Revoke ALL active access for a guest user in a single operation
 * USER perspective - owner revokes all access at once (bulk operation)
 *
 * This is more efficient than calling revokeGuestAccess for each device:
 * - Single API call instead of N calls
 * - Single database transaction
 * - No race conditions or partial failures
 *
 * @param guestEmail - Guest email address
 * @returns Revoke confirmation with count and device list
 */
export async function revokeAllGuestAccess(guestEmail: string): Promise<{
  guest_email: string
  revoked_count: number
  devices_revoked: string[]
  revoked_at: string
  message: string
}> {
  try {
    console.log('🚫 Revoking ALL guest access for:', guestEmail)

    const response = await apiRequestProtected('DELETE', `/user/guests/revoke-all/${encodeURIComponent(guestEmail)}`)

    console.log('✅ All guest access revoked:', response)
    return response.data || response
  } catch (error: any) {
    console.error('❌ Failed to revoke all guest access:', error)
    throw error
  }
}

/**
 * Delete all access records for a guest user
 * USER perspective - owner deletes revoked guest from list entirely
 *
 * @param guestEmail - Guest email address
 * @returns Delete confirmation response
 */
export async function deleteGuestAccess(guestEmail: string): Promise<{ deleted_count: number; message: string }> {
  try {
    console.log('🗑️ Deleting all guest access for:', guestEmail)

    const response = await apiRequestProtected('DELETE', `/user/guests/delete/${encodeURIComponent(guestEmail)}`)

    console.log('✅ Guest access deleted:', response)
    return response.data || response
  } catch (error: any) {
    console.error('❌ Failed to delete guest access:', error)
    throw error
  }
}

/**
 * Get devices shared with the current guest user
 * GUEST perspective - view devices shared with them
 *
 * @returns List of shared devices grouped by owner
 */
export async function getSharedDevices(): Promise<SharedDevicesResponse> {
  try {
    const response = await apiRequestProtected('GET', '/guest/devices')

    // API returns { success: true, data: { devices, groupedByOwner } }
    const data = response.data || response

    return {
      devices: data.devices || [],
      groupedByOwner: data.groupedByOwner || {},
    }
  } catch (error: any) {
    console.error('❌ Failed to get shared devices:', error)
    throw error
  }
}

/**
 * Pending invitation type from the API
 */
export interface PendingInvitation {
  id: string
  email: string
  role: string
  status: string
  created_at: string
  expires_at: string
  is_expired: boolean
  pending_devices: Array<{
    device_id: number
    device_name: string
    access_level: string
    invited_at: string
  }>
}

export interface PendingInvitationsResponse {
  invitations: PendingInvitation[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

/**
 * Get pending invitations (users who haven't registered yet)
 * USER perspective - view who you've invited that hasn't registered
 *
 * @param page - Page number (1-indexed)
 * @param limit - Results per page
 * @returns Paginated list of pending invitations
 */
export async function getPendingInvitations(page: number = 1, limit: number = 50): Promise<PendingInvitationsResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    const response = await apiRequestProtected('GET', `/user/pending-invitations?${params.toString()}`)

    // API returns { success: true, data: { invitations, pagination } }
    const apiResponse = response.data || response

    return {
      invitations: apiResponse.invitations || [],
      pagination: apiResponse.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    }
  } catch (error: any) {
    console.error('Failed to get pending invitations:', error)
    throw error
  }
}

/**
 * Delete a pending invitation
 * USER perspective - delete an invitation you sent that hasn't been accepted
 *
 * This removes:
 * - The pending_accounts record
 * - Associated pending_device_access records
 * - The Supabase auth user (if created)
 *
 * @param id - Pending account UUID
 * @returns Delete confirmation response
 */
export async function deletePendingInvitation(
  id: string
): Promise<{ deleted: { id: string; email: string }; message: string }> {
  try {
    console.log('🗑️ Deleting pending invitation:', id)

    const response = await apiRequestProtected('DELETE', `/user/pending-invitations/${id}`)

    console.log('✅ Pending invitation deleted:', response)
    return response.data || response
  } catch (error: any) {
    console.error('Failed to delete pending invitation:', error)
    throw error
  }
}

/**
 * Resend invitation email to a pending guest
 * USER perspective - resend the password reset email
 *
 * @param id - Pending account UUID
 * @returns Resend confirmation response
 */
export async function resendInvitation(id: string): Promise<{ email: string; message: string }> {
  try {
    console.log('📧 Resending invitation:', id)

    const response = await apiRequestProtected('POST', `/user/pending-invitations/${id}/resend`)

    console.log('✅ Invitation resent:', response)
    return response.data || response
  } catch (error: any) {
    console.error('Failed to resend invitation:', error)
    throw error
  }
}

/**
 * Guest API service object
 */
export const guestApi = {
  inviteGuest,
  listGuests,
  revokeGuestAccess,
  revokeAllGuestAccess,
  deleteGuestAccess,
  getSharedDevices,
  getPendingInvitations,
  deletePendingInvitation,
  resendInvitation,
}
